import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  KPIsResponse,
  StatusDistribution,
  StatusDistributionResponse,
  CreationTrend,
  CreationTrendResponse,
  RecentActivity,
  PoliciesResponse,
  PolicyListItem,
  Policy,
  ErrorPoliciesResponse,
  AnalyticsAggregation,
  RefreshPolicyRequest,
  RefreshPolicyResponse,
  DashboardQueryParams,
  PoliciesQueryParams,
  SearchPoliciesQueryParams,
  AnalyticsQueryParams,
  ErrorPoliciesQueryParams,
  RecentActivityQueryParams
} from '../models/seguros.interface';

@Injectable({
  providedIn: 'root'
})
export class SegurosApiService {
  private readonly baseUrl = `${environment.BASE_LA}/api/insurance`;

  constructor(private http: HttpClient) {}

  // Dashboard endpoints
  getDashboardKPIs(params?: DashboardQueryParams): Observable<KPIsResponse> {
    let httpParams = new HttpParams();
    if (params?.date_from) httpParams = httpParams.set('date_from', params.date_from);
    if (params?.date_to) httpParams = httpParams.set('date_to', params.date_to);
    if (params?.insurer_code) httpParams = httpParams.set('insurer_code', params.insurer_code);
    
    const url = `${this.baseUrl}/dashboard/kpis`;
    console.log('🌐 Calling KPIs API:', url, 'with params:', params);
    
    return this.http.get<KPIsResponse>(url, { params: httpParams });
  }

  getStatusDistribution(params?: DashboardQueryParams): Observable<StatusDistributionResponse> {
    let httpParams = new HttpParams();
    if (params?.date_from) httpParams = httpParams.set('date_from', params.date_from);
    if (params?.date_to) httpParams = httpParams.set('date_to', params.date_to);
    if (params?.insurer_code) httpParams = httpParams.set('insurer_code', params.insurer_code);
    
    return this.http.get<StatusDistributionResponse>(`${this.baseUrl}/dashboard/status-distribution`, { params: httpParams });
  }

  getCreationTrend(params?: DashboardQueryParams): Observable<CreationTrendResponse> {
    let httpParams = new HttpParams();
    if (params?.date_from) httpParams = httpParams.set('startDate', params.date_from);
    if (params?.date_to) httpParams = httpParams.set('endDate', params.date_to);
    if (params?.insurer_code) httpParams = httpParams.set('insurer_code', params.insurer_code);
    
    const url = `${this.baseUrl}/dashboard/creation-trend`;
    console.log('🌐 Calling Creation Trend API:', url, 'with params:', params);
    
    return this.http.get<CreationTrendResponse>(url, { params: httpParams });
  }

  getRecentActivity(params?: RecentActivityQueryParams): Observable<RecentActivity[]> {
    let httpParams = new HttpParams();
    if (params?.limit) httpParams = httpParams.set('limit', params.limit.toString());
    if (params?.insurer_code) httpParams = httpParams.set('insurer_code', params.insurer_code);
    
    const url = `${this.baseUrl}/dashboard/recent-activity`;
    console.log('🌐 Calling Recent Activity API:', url, 'with params:', params);
    
    return this.http.get<RecentActivity[]>(url, { params: httpParams });
  }

  // Policies management endpoints
  getPolicies(params?: PoliciesQueryParams): Observable<PoliciesResponse> {
    let httpParams = new HttpParams();
    if (params?.page) httpParams = httpParams.set('page', params.page.toString());
    if (params?.limit) httpParams = httpParams.set('limit', params.limit.toString());
    if (params?.status) httpParams = httpParams.set('status', params.status);
    if (params?.search) httpParams = httpParams.set('search', params.search);
    if (params?.date_from) httpParams = httpParams.set('date_from', params.date_from);
    if (params?.date_to) httpParams = httpParams.set('date_to', params.date_to);
    if (params?.insurer_code) httpParams = httpParams.set('insurer_code', params.insurer_code);
    
    return this.http.get<PoliciesResponse>(`${this.baseUrl}/policies`, { params: httpParams });
  }

  exportPoliciesCsv(params?: { date_from?: string; date_to?: string; insurer_code?: string; search?: string; status?: string }): Observable<Blob> {
    let httpParams = new HttpParams();
    if (params?.date_from) httpParams = httpParams.set('date_from', params.date_from);
    if (params?.date_to) httpParams = httpParams.set('date_to', params.date_to);
    if (params?.insurer_code) httpParams = httpParams.set('insurer_code', params.insurer_code);
    if (params?.search) httpParams = httpParams.set('search', params.search);
    if (params?.status) httpParams = httpParams.set('status', params.status);
    
    return this.http.get(`${this.baseUrl}/policies/export`, { params: httpParams, responseType: 'blob' });
  }

  getPolicyDetail(id: string): Observable<Policy> {
    return this.http.get<Policy>(`${this.baseUrl}/policies/${id}`);
  }

  // Advanced search endpoint
  searchPolicies(params?: SearchPoliciesQueryParams): Observable<PoliciesResponse> {
    let httpParams = new HttpParams();
    
    // Parámetros de búsqueda
    if (params?.query) httpParams = httpParams.set('query', params.query);
    if (params?.policy_code) httpParams = httpParams.set('policy_code', params.policy_code);
    if (params?.identity) httpParams = httpParams.set('identity', params.identity);
    if (params?.policy_status) httpParams = httpParams.set('policy_status', params.policy_status);
    
    // Parámetros de paginación
    if (params?.page) httpParams = httpParams.set('page', params.page.toString());
    if (params?.limit) httpParams = httpParams.set('limit', params.limit.toString());
    
    // Parámetros de fecha
    if (params?.date_from) httpParams = httpParams.set('date_from', params.date_from);
    if (params?.date_to) httpParams = httpParams.set('date_to', params.date_to);
    
    // Parámetro de aseguradora
    if (params?.insurer_code) httpParams = httpParams.set('insurer_code', params.insurer_code);

    // Filtros del drill-down del dashboard (plan 01 Acts 1+2+3).
    if (params?.plan_type) httpParams = httpParams.set('plan_type', params.plan_type);
    if (params?.frequency) httpParams = httpParams.set('frequency', params.frequency);
    if (params?.notification_status) httpParams = httpParams.set('notification_status', params.notification_status);

    console.log('🔍 API Call - Search Policies:', `${this.baseUrl}/policies/search`, { params: httpParams.toString() });
    
    return this.http.get<PoliciesResponse>(`${this.baseUrl}/policies/search`, { params: httpParams })
      .pipe(
        tap(response => console.log('✅ Search Policies Response:', response)),
        catchError(error => {
          console.error('❌ Search Policies Error:', error);
          throw error;
        })
      );
  }

  // Contactos por estado (drill-down del dashboard).
  // params: ver ContactsQueryParams en seguros.interface.ts
  getPoliciesContacts(params: any): Observable<any> {
    let httpParams = new HttpParams();
    if (params?.policy_status) httpParams = httpParams.set('policy_status', Array.isArray(params.policy_status) ? params.policy_status.join(',') : params.policy_status);
    if (params?.page) httpParams = httpParams.set('page', params.page.toString());
    if (params?.limit) httpParams = httpParams.set('limit', params.limit.toString());
    if (params?.date_from) httpParams = httpParams.set('date_from', params.date_from);
    if (params?.date_to) httpParams = httpParams.set('date_to', params.date_to);
    if (params?.insurer_code) httpParams = httpParams.set('insurer_code', params.insurer_code);
    if (params?.plan_type) httpParams = httpParams.set('plan_type', params.plan_type);
    if (params?.frequency) httpParams = httpParams.set('frequency', params.frequency);
    if (params?.notification_status) httpParams = httpParams.set('notification_status', params.notification_status);
    if (params?.min_days_inactive != null) httpParams = httpParams.set('min_days_inactive', String(params.min_days_inactive));
    if (params?.has_phone) httpParams = httpParams.set('has_phone', 'true');
    if (params?.has_email) httpParams = httpParams.set('has_email', 'true');
    if (params?.search) httpParams = httpParams.set('search', params.search);
    return this.http.get(`${this.baseUrl}/policies/contacts`, { params: httpParams });
  }

  // URL absoluto del export — el componente lo usa con `window.open` para que
  // el navegador maneje la descarga sin pasar por interceptors.
  getPoliciesContactsExportUrl(params: any): string {
    const usp = new URLSearchParams();
    if (params?.policy_status) usp.set('policy_status', Array.isArray(params.policy_status) ? params.policy_status.join(',') : params.policy_status);
    if (params?.date_from) usp.set('date_from', params.date_from);
    if (params?.date_to) usp.set('date_to', params.date_to);
    if (params?.insurer_code) usp.set('insurer_code', params.insurer_code);
    if (params?.plan_type) usp.set('plan_type', params.plan_type);
    if (params?.frequency) usp.set('frequency', params.frequency);
    if (params?.notification_status) usp.set('notification_status', params.notification_status);
    if (params?.min_days_inactive != null) usp.set('min_days_inactive', String(params.min_days_inactive));
    if (params?.has_phone) usp.set('has_phone', 'true');
    if (params?.has_email) usp.set('has_email', 'true');
    if (params?.search) usp.set('search', params.search);
    return `${this.baseUrl}/policies/contacts/export?${usp.toString()}`;
  }

  // Error management endpoints
  getErrorPolicies(params?: ErrorPoliciesQueryParams): Observable<ErrorPoliciesResponse> {
    let httpParams = new HttpParams();
    if (params?.page) httpParams = httpParams.set('page', params.page.toString());
    if (params?.limit) httpParams = httpParams.set('limit', params.limit.toString());
    if (params?.insurer_code) httpParams = httpParams.set('insurer_code', params.insurer_code);
    if (params?.date_from) httpParams = httpParams.set('date_from', params.date_from);
    if (params?.date_to) httpParams = httpParams.set('date_to', params.date_to);
    if (params?.query) httpParams = httpParams.set('query', params.query);
    if (params?.policy_code) httpParams = httpParams.set('policy_code', params.policy_code);
    if (params?.identity) httpParams = httpParams.set('identity', params.identity);
    if (params?.policy_status) httpParams = httpParams.set('policy_status', params.policy_status);
    
    console.log('🔍 API Call - Error Policies:', `${this.baseUrl}/policies/errors`, { params: httpParams.toString() });
    
    return this.http.get<ErrorPoliciesResponse>(`${this.baseUrl}/policies/errors`, { params: httpParams })
      .pipe(
        tap(response => {
          console.log('✅ FULL Error Policies Response:', response);
          console.log('✅ Response data array:', response?.data);
          console.log('✅ Response pagination:', response?.pagination);
          console.log('📊 IMPORTANTE - Verificar paginación:');
          console.log('   - currentPage:', response?.pagination?.currentPage);
          console.log('   - totalPages:', response?.pagination?.totalPages);
          console.log('   - totalItems:', response?.pagination?.totalItems);
          console.log('   - limit:', response?.pagination?.limit);
          console.log('   - Data length:', response?.data?.length);
        }),
        catchError(error => {
          console.error('❌ Error Policies Error:', error);
          throw error;
        })
      );
  }

  // Payment error management endpoints
  getPaymentErrorPolicies(params?: ErrorPoliciesQueryParams): Observable<ErrorPoliciesResponse> {
    let httpParams = new HttpParams();
    if (params?.page) httpParams = httpParams.set('page', params.page.toString());
    if (params?.limit) httpParams = httpParams.set('limit', params.limit.toString());
    if (params?.insurer_code) httpParams = httpParams.set('insurer_code', params.insurer_code);
    if (params?.date_from) httpParams = httpParams.set('date_from', params.date_from);
    if (params?.date_to) httpParams = httpParams.set('date_to', params.date_to);
    if (params?.query) httpParams = httpParams.set('query', params.query);
    if (params?.policy_code) httpParams = httpParams.set('policy_code', params.policy_code);
    if (params?.identity) httpParams = httpParams.set('identity', params.identity);
    if (params?.policy_status) httpParams = httpParams.set('policy_status', params.policy_status);
    
    console.log('🔍 API Call - Payment Error Policies:', `${this.baseUrl}/policies/payment-errors`, { params: httpParams.toString() });
    
    return this.http.get<ErrorPoliciesResponse>(`${this.baseUrl}/policies/payment-errors`, { params: httpParams })
      .pipe(
        tap(response => {
          console.log('✅ FULL Payment Error Policies Response:', response);
          console.log('✅ Response data array:', response?.data);
          console.log('✅ Response pagination:', response?.pagination);
          console.log('📊 IMPORTANTE - Verificar paginación:');
          console.log('   - currentPage:', response?.pagination?.currentPage);
          console.log('   - totalPages:', response?.pagination?.totalPages);
          console.log('   - totalItems:', response?.pagination?.totalItems);
          console.log('   - limit:', response?.pagination?.limit);
          console.log('   - Data length:', response?.data?.length);
        }),
        catchError(error => {
          console.error('❌ Payment Error Policies Error:', error);
          throw error;
        })
      );
  }

  // Error resolution endpoints
  resolveEmissionError(policyId: string, resolutionComment: string): Observable<any> {
    const body = {
      resolution_comment: resolutionComment
    };
    
    console.log('🔧 API Call - Resolve Emission Error:', `${this.baseUrl}/policies/${policyId}/resolve-emission-error`, body);
    
    return this.http.put<any>(`${this.baseUrl}/policies/${policyId}/resolve-emission-error`, body)
      .pipe(
        tap(response => {
          console.log('✅ Emission Error Resolved:', response);
        }),
        catchError(error => {
          console.error('❌ Resolve Emission Error Failed:', error);
          throw error;
        })
      );
  }

  // Verification error management endpoints
  getVerificationErrorPolicies(params?: ErrorPoliciesQueryParams): Observable<ErrorPoliciesResponse> {
    let httpParams = new HttpParams();
    if (params?.page) httpParams = httpParams.set('page', params.page.toString());
    if (params?.limit) httpParams = httpParams.set('limit', params.limit.toString());
    if (params?.insurer_code) httpParams = httpParams.set('insurer_code', params.insurer_code);
    if (params?.date_from) httpParams = httpParams.set('date_from', params.date_from);
    if (params?.date_to) httpParams = httpParams.set('date_to', params.date_to);
    if (params?.query) httpParams = httpParams.set('query', params.query);
    
    console.log('🔍 API Call - Verification Error Policies:', `${this.baseUrl}/policies/verification-errors`, { params: httpParams.toString() });
    
    return this.http.get<ErrorPoliciesResponse>(`${this.baseUrl}/policies/verification-errors`, { params: httpParams })
      .pipe(
        tap(response => {
          console.log('✅ Verification Error Policies Response:', response);
        }),
        catchError(error => {
          console.error('❌ Verification Error Policies Error:', error);
          throw error;
        })
      );
  }

  // Vehicle use error management endpoints
  getVehicleUseErrorPolicies(params?: ErrorPoliciesQueryParams & { error_code?: string }): Observable<ErrorPoliciesResponse> {
    let httpParams = new HttpParams();
    if (params?.page) httpParams = httpParams.set('page', params.page.toString());
    if (params?.limit) httpParams = httpParams.set('limit', params.limit.toString());
    if (params?.insurer_code) httpParams = httpParams.set('insurer_code', params.insurer_code);
    if (params?.date_from) httpParams = httpParams.set('date_from', params.date_from);
    if (params?.date_to) httpParams = httpParams.set('date_to', params.date_to);
    if (params?.query) httpParams = httpParams.set('query', params.query);
    if (params?.error_code) httpParams = httpParams.set('error_code', params.error_code);
    
    console.log('🔍 API Call - Vehicle Use Error Policies:', `${this.baseUrl}/policies/vehicle-use-errors`, { params: httpParams.toString() });
    
    return this.http.get<ErrorPoliciesResponse>(`${this.baseUrl}/policies/vehicle-use-errors`, { params: httpParams })
      .pipe(
        tap(response => {
          console.log('✅ Vehicle Use Error Policies Response:', response);
        }),
        catchError(error => {
          console.error('❌ Vehicle Use Error Policies Error:', error);
          throw error;
        })
      );
  }

  // Default address policies endpoint
  getDefaultAddressPolicies(params?: ErrorPoliciesQueryParams): Observable<ErrorPoliciesResponse> {
    let httpParams = new HttpParams();
    if (params?.page) httpParams = httpParams.set('page', params.page.toString());
    if (params?.limit) httpParams = httpParams.set('limit', params.limit.toString());
    if (params?.insurer_code) httpParams = httpParams.set('insurer_code', params.insurer_code);
    if (params?.date_from) httpParams = httpParams.set('date_from', params.date_from);
    if (params?.date_to) httpParams = httpParams.set('date_to', params.date_to);
    if (params?.query) httpParams = httpParams.set('query', params.query);
    
    console.log('🔍 API Call - Default Address Policies:', `${this.baseUrl}/policies/default-address`, { params: httpParams.toString() });
    
    return this.http.get<ErrorPoliciesResponse>(`${this.baseUrl}/policies/default-address`, { params: httpParams })
      .pipe(
        tap(response => {
          console.log('✅ Default Address Policies Response:', response);
        }),
        catchError(error => {
          console.error('❌ Default Address Policies Error:', error);
          throw error;
        })
      );
  }

  resolvePaymentError(policyId: string, resolutionComment: string): Observable<any> {
    const body = {
      resolution_comment: resolutionComment
    };
    
    console.log('🔧 API Call - Resolve Payment Error:', `${this.baseUrl}/policies/${policyId}/resolve-payment-error`, body);
    
    return this.http.put<any>(`${this.baseUrl}/policies/${policyId}/resolve-payment-error`, body)
      .pipe(
        tap(response => {
          console.log('✅ Payment Error Resolved:', response);
        }),
        catchError(error => {
          console.error('❌ Resolve Payment Error Failed:', error);
          throw error;
        })
      );
  }

  retryPaymentNotification(policyCode: string): Observable<any> {
    const body = { policy_code: policyCode };
    
    console.log('🔄 API Call - Retry Payment Notification:', `${this.baseUrl}/retry_payment_notification`, body);
    
    return this.http.post<any>(`${this.baseUrl}/retry_payment_notification`, body)
      .pipe(
        tap(response => {
          console.log('✅ Payment Notification Retry Response:', response);
        }),
        catchError(error => {
          console.error('❌ Retry Payment Notification Failed:', error);
          throw error;
        })
      );
  }

  // Analytics endpoints
  getAnalyticsAggregation(params: AnalyticsQueryParams): Observable<AnalyticsAggregation[]> {
    let httpParams = new HttpParams();
    httpParams = httpParams.set('type', params.type);
    if (params.date_from) httpParams = httpParams.set('date_from', params.date_from);
    if (params.date_to) httpParams = httpParams.set('date_to', params.date_to);
    if (params.insurer_code) httpParams = httpParams.set('insurer_code', params.insurer_code);
    // Filtros nuevos del Plan C (chips multi-select del análisis demográfico).
    if (params.policy_status) httpParams = httpParams.set('policy_status', params.policy_status);
    if (params.notification_status) httpParams = httpParams.set('notification_status', params.notification_status);
    if (params.frequency) httpParams = httpParams.set('frequency', params.frequency);

    return this.http.get<AnalyticsAggregation[]>(`${this.baseUrl}/analytics/aggregate`, { params: httpParams });
  }

  // Utility endpoints
  refreshPolicyView(request: RefreshPolicyRequest): Observable<RefreshPolicyResponse> {
    return this.http.post<RefreshPolicyResponse>(`${this.baseUrl}/refresh_policy_view`, request);
  }

  resumeSubscribeSM(policyCode: string): Observable<any> {
    const url = `${this.baseUrl}/resume_subscribe_sm`;
    const body = { policy_code: policyCode };
    console.log('🌐 Calling Resume Subscribe SM API:', url, 'with body:', body);

    return this.http.post<any>(url, body);
  }

  /**
   * Reintenta la emisión de una póliza RCV/SC atascada.
   * Delega al servicio interno resumeSubscribeSCService del Plan 4.
   * Ver work-plans/sc-flow-update/camino-feliz/plan-7-consola-estados-y-metadata.md
   */
  retryEmissionSC(policyCode: string): Observable<any> {
    const url = `${this.baseUrl}/retry_emission_sc`;
    const body = { policy_code: policyCode };
    console.log('🌐 Calling Retry Emission SC API:', url, 'with body:', body);

    return this.http.post<any>(url, body);
  }

  // Convenience methods for specific analytics types
  getGeographyAnalytics(params?: DashboardQueryParams): Observable<AnalyticsAggregation[]> {
    return this.getAnalyticsAggregation({ type: 'geography', ...params });
  }

  getAgeAnalytics(params?: DashboardQueryParams): Observable<AnalyticsAggregation[]> {
    return this.getAnalyticsAggregation({ type: 'age', ...params });
  }

  getProfessionAnalytics(params?: DashboardQueryParams): Observable<AnalyticsAggregation[]> {
    return this.getAnalyticsAggregation({ type: 'profession', ...params });
  }

  getProductAnalytics(params?: DashboardQueryParams): Observable<AnalyticsAggregation[]> {
    return this.getAnalyticsAggregation({ type: 'product', ...params });
  }

  getVehicleBrandAnalytics(params?: DashboardQueryParams): Observable<AnalyticsAggregation[]> {
    return this.getAnalyticsAggregation({ type: 'vehicleBrand', ...params });
  }

  getVehicleAgeAnalytics(params?: DashboardQueryParams): Observable<AnalyticsAggregation[]> {
    return this.getAnalyticsAggregation({ type: 'vehicleAge', ...params });
  }

  // Insurers endpoints (original - para dropdown)
  getInsurers(): Observable<any[]> {
    return this.http.get<any[]>(`${this.baseUrl}/insurers`);
  }

  // Insurers Console Management endpoints
  getInsurersConsole(params?: any): Observable<any> {
    let httpParams = new HttpParams();
    if (params?.page) httpParams = httpParams.set('page', params.page.toString());
    if (params?.limit) httpParams = httpParams.set('limit', params.limit.toString());
    if (params?.sortBy) httpParams = httpParams.set('sortBy', params.sortBy);
    if (params?.sortOrder) httpParams = httpParams.set('sortOrder', params.sortOrder);
    if (params?.search) httpParams = httpParams.set('search', params.search);
    if (params?.status !== undefined && params?.status !== null) {
      httpParams = httpParams.set('status', params.status.toString());
    }
    
    return this.http.get<any>(`${this.baseUrl}/console/insurers`, { params: httpParams });
  }

  getInsurerById(id: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/console/insurers/${id}`);
  }

  updateInsurer(id: string, data: any): Observable<any> {
    return this.http.put<any>(`${this.baseUrl}/console/insurers/${id}`, data);
  }

  toggleInsurerStatus(id: string, status: boolean): Observable<any> {
    return this.http.patch<any>(`${this.baseUrl}/console/insurers/${id}/status`, { status });
  }

  getProducts(params: any): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/console/products`, { params });
  }

  getProductById(id: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/console/products/${id}`);
  }

  updateProduct(id: string, data: any): Observable<any> {
    return this.http.put<any>(`${this.baseUrl}/console/products/${id}`, data);
  }

  toggleProductStatus(id: string, status: boolean): Observable<any> {
    return this.http.patch<any>(`${this.baseUrl}/console/products/${id}/status`, { status });
  }

  // Sync log endpoint
  getLatestSyncLog(params?: { page?: number; limit?: number }): Observable<any> {
    let httpParams = new HttpParams();
    if (params?.page) httpParams = httpParams.set('page', params.page.toString());
    if (params?.limit) httpParams = httpParams.set('limit', params.limit.toString());
    
    return this.http.get<any>(`${this.baseUrl}/sync_log/latest`, { params: httpParams });
  }

  // Export CSV endpoints (streaming)
  exportEmissionErrorsCsv(params?: { date_from?: string; date_to?: string; insurer_code?: string }): Observable<Blob> {
    let httpParams = new HttpParams();
    if (params?.date_from) httpParams = httpParams.set('date_from', params.date_from);
    if (params?.date_to) httpParams = httpParams.set('date_to', params.date_to);
    if (params?.insurer_code) httpParams = httpParams.set('insurer_code', params.insurer_code);
    return this.http.get(`${this.baseUrl}/policies/errors/export`, { params: httpParams, responseType: 'blob' });
  }

  exportPaymentErrorsCsv(params?: { date_from?: string; date_to?: string; insurer_code?: string }): Observable<Blob> {
    let httpParams = new HttpParams();
    if (params?.date_from) httpParams = httpParams.set('date_from', params.date_from);
    if (params?.date_to) httpParams = httpParams.set('date_to', params.date_to);
    if (params?.insurer_code) httpParams = httpParams.set('insurer_code', params.insurer_code);
    return this.http.get(`${this.baseUrl}/policies/payment-errors/export`, { params: httpParams, responseType: 'blob' });
  }

  exportVerificationErrorsCsv(params?: { date_from?: string; date_to?: string; insurer_code?: string }): Observable<Blob> {
    let httpParams = new HttpParams();
    if (params?.date_from) httpParams = httpParams.set('date_from', params.date_from);
    if (params?.date_to) httpParams = httpParams.set('date_to', params.date_to);
    if (params?.insurer_code) httpParams = httpParams.set('insurer_code', params.insurer_code);
    return this.http.get(`${this.baseUrl}/policies/verification-errors/export`, { params: httpParams, responseType: 'blob' });
  }

  exportVehicleUseErrorsCsv(params?: { date_from?: string; date_to?: string; insurer_code?: string }): Observable<Blob> {
    let httpParams = new HttpParams();
    if (params?.date_from) httpParams = httpParams.set('date_from', params.date_from);
    if (params?.date_to) httpParams = httpParams.set('date_to', params.date_to);
    if (params?.insurer_code) httpParams = httpParams.set('insurer_code', params.insurer_code);
    return this.http.get(`${this.baseUrl}/policies/vehicle-use-errors/export`, { params: httpParams, responseType: 'blob' });
  }

  exportDefaultAddressCsv(params?: { date_from?: string; date_to?: string; insurer_code?: string }): Observable<Blob> {
    let httpParams = new HttpParams();
    if (params?.date_from) httpParams = httpParams.set('date_from', params.date_from);
    if (params?.date_to) httpParams = httpParams.set('date_to', params.date_to);
    if (params?.insurer_code) httpParams = httpParams.set('insurer_code', params.insurer_code);
    return this.http.get(`${this.baseUrl}/policies/default-address/export`, { params: httpParams, responseType: 'blob' });
  }

  exportAnnulledPoliciesCsv(params?: { startDate?: string; endDate?: string }): Observable<Blob> {
    let httpParams = new HttpParams();
    if (params?.startDate) httpParams = httpParams.set('startDate', params.startDate);
    if (params?.endDate) httpParams = httpParams.set('endDate', params.endDate);
    return this.http.get(`${this.baseUrl}/annulled_policies/export`, { params: httpParams, responseType: 'blob' });
  }

  // Annulled policies endpoint
  getAnnulledPolicies(params: { startDate?: string; endDate?: string; page?: number; limit?: number }): Observable<any> {
    let httpParams = new HttpParams();
    // Solo agregar fechas si están definidas (no undefined)
    if (params.startDate !== undefined && params.startDate !== null) {
      httpParams = httpParams.set('startDate', params.startDate);
    }
    if (params.endDate !== undefined && params.endDate !== null) {
      httpParams = httpParams.set('endDate', params.endDate);
    }
    if (params?.page) httpParams = httpParams.set('page', params.page.toString());
    if (params?.limit) httpParams = httpParams.set('limit', params.limit.toString());
    
    const url = `${this.baseUrl}/annulled_policies`;
    console.log('🌐 Calling Annulled Policies API:', url, 'with params:', params);
    
    return this.http.get<any>(url, { params: httpParams }).pipe(
      catchError((error) => {
        // Si es error 204 o respuesta vacía, retornar estructura vacía válida
        if (error.status === 204 || error.status === 0) {
          console.log('ℹ️ No content (204) - returning empty structure');
          return of({
            success: true,
            data: {
              policies: [],
              pagination: {
                currentPage: params.page || 1,
                totalPages: 1,
                totalItems: 0,
                itemsPerPage: params.limit || 20
              }
            }
          });
        }
        // Para otros errores, propagar
        throw error;
      })
    );
  }

  // SC Relations endpoints
  getSCRelationByDate(fePago: string): Observable<any> {
    const httpParams = new HttpParams().set('fePago', fePago);
    const url = `${this.baseUrl}/sc/relation-by-date`;
    console.log('🌐 Calling SC Relation By Date API:', url, 'with date:', fePago);
    return this.http.get(url, { params: httpParams });
  }

  fetchSCRelationFromAPI(fePago: string): Observable<any> {
    const httpParams = new HttpParams().set('fePago', fePago);
    const url = `${this.baseUrl}/sc/relations`;
    console.log('🌐 Fetching SC Relation from API:', url, 'with date:', fePago);
    return this.http.get(url, { params: httpParams });
  }

  saveSCDeposits(data: any): Observable<any> {
    const url = `${this.baseUrl}/sc/deposits`;
    console.log('🌐 Calling Save SC Deposits API:', url, 'with data:', data);
    return this.http.post<any>(url, data);
  }

  reportSCDeposits(nuRelacion: number): Observable<any> {
    const url = `${this.baseUrl}/sc/report-deposits`;
    console.log('🌐 Calling Report SC Deposits API:', url, 'with nuRelacion:', nuRelacion);
    
    return this.http.post<any>(url, { nuRelacion });
  }

  updateSCDeposit(depositId: string, depositData: any): Observable<any> {
    const url = `${this.baseUrl}/sc/deposit/${depositId}`;
    console.log('🌐 Calling Update SC Deposit API:', url, 'with data:', depositData);
    
    return this.http.put<any>(url, depositData);
  }

  deleteSCDeposit(depositId: string): Observable<any> {
    const url = `${this.baseUrl}/sc/deposit/${depositId}`;
    console.log('🌐 Calling Delete SC Deposit API:', url);
    
    return this.http.delete<any>(url);
  }

  getSCPaymentsByDate(fePago: string): Observable<any> {
    const httpParams = new HttpParams().set('fePago', fePago);
    const url = `${this.baseUrl}/sc/payments-by-date`;
    console.log('🌐 Calling SC Payments By Date API:', url, 'with date:', fePago);
    
    return this.http.get<any>(url, { params: httpParams });
  }

  // Banks endpoint
  getBanks(): Observable<any> {
    const url = `${environment.BASE_LA}/api/invoice-credit/banks`;
    console.log('🌐 Calling Banks API:', url);

    return this.http.get<any>(url);
  }

  // ═══════════════════════════════════════════════════════════════════════
  // TuDoc tracking / abandonos (plan 21)
  // ═══════════════════════════════════════════════════════════════════════

  getTudocTraces(params?: import('../models/seguros.interface').TudocTracesQuery)
    : Observable<import('../models/seguros.interface').TudocTracesResponse> {
    let httpParams = new HttpParams();
    if (params?.person_code) httpParams = httpParams.set('person_code', params.person_code);
    if (params?.session_id) httpParams = httpParams.set('session_id', params.session_id);
    if (params?.policy_code) httpParams = httpParams.set('policy_code', params.policy_code);
    if (params?.step) httpParams = httpParams.set('step', params.step);
    if (params?.startDate) httpParams = httpParams.set('startDate', params.startDate);
    if (params?.endDate) httpParams = httpParams.set('endDate', params.endDate);
    if (params?.page) httpParams = httpParams.set('page', String(params.page));
    if (params?.limit) httpParams = httpParams.set('limit', String(params.limit));
    return this.http.get<import('../models/seguros.interface').TudocTracesResponse>(
      `${this.baseUrl}/tudoc/traces`, { params: httpParams }
    );
  }

  getTudocFunnelSummary(params?: import('../models/seguros.interface').TudocFunnelQuery)
    : Observable<import('../models/seguros.interface').TudocFunnelSummaryResponse> {
    let httpParams = new HttpParams();
    if (params?.startDate) httpParams = httpParams.set('startDate', params.startDate);
    if (params?.endDate) httpParams = httpParams.set('endDate', params.endDate);
    return this.http.get<import('../models/seguros.interface').TudocFunnelSummaryResponse>(
      `${this.baseUrl}/tudoc/funnel-summary`, { params: httpParams }
    );
  }

  getTudocAbandonedClients(params?: import('../models/seguros.interface').TudocAbandonedClientsQuery)
    : Observable<import('../models/seguros.interface').TudocAbandonedClientsResponse> {
    let httpParams = new HttpParams();
    if (params?.startDate) httpParams = httpParams.set('startDate', params.startDate);
    if (params?.endDate) httpParams = httpParams.set('endDate', params.endDate);
    if (params?.step) httpParams = httpParams.set('step', params.step);
    if (params?.page) httpParams = httpParams.set('page', String(params.page));
    if (params?.limit) httpParams = httpParams.set('limit', String(params.limit));
    return this.http.get<import('../models/seguros.interface').TudocAbandonedClientsResponse>(
      `${this.baseUrl}/tudoc/abandoned-clients`, { params: httpParams }
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  // TuDoc — Activaciones pendientes post-pago (Plan 28, 2026-08-03)
  // ═══════════════════════════════════════════════════════════════════════

  getTudocFailedAffiliations(params?: {
    startDate?: string;
    endDate?: string;
    status?: 'failed' | 'escalated' | 'resolved' | 'all';
    personSearch?: string;
    page?: number;
    pageSize?: number;
  }): Observable<any> {
    let httpParams = new HttpParams();
    if (params?.startDate) httpParams = httpParams.set('startDate', params.startDate);
    if (params?.endDate) httpParams = httpParams.set('endDate', params.endDate);
    if (params?.status) httpParams = httpParams.set('status', params.status);
    if (params?.personSearch) httpParams = httpParams.set('personSearch', params.personSearch);
    if (params?.page) httpParams = httpParams.set('page', String(params.page));
    if (params?.pageSize) httpParams = httpParams.set('pageSize', String(params.pageSize));
    return this.http.get<any>(`${this.baseUrl}/tudoc/failed-affiliations`, { params: httpParams });
  }

  retryTudocAffiliationAdmin(policyCode: string, reason?: string): Observable<any> {
    const body: any = {};
    if (reason) body.reason = reason;
    return this.http.post<any>(`${this.baseUrl}/tudoc/failed-affiliations/${encodeURIComponent(policyCode)}/retry`, body);
  }

  markTudocAffiliationResolved(policyCode: string, comment: string): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/tudoc/failed-affiliations/${encodeURIComponent(policyCode)}/mark-resolved`, { comment });
  }

  exportTudocFailedAffiliationsCsv(params?: {
    startDate?: string;
    endDate?: string;
    status?: 'failed' | 'escalated' | 'resolved' | 'all';
    personSearch?: string;
  }): Observable<Blob> {
    let httpParams = new HttpParams();
    if (params?.startDate) httpParams = httpParams.set('startDate', params.startDate);
    if (params?.endDate) httpParams = httpParams.set('endDate', params.endDate);
    if (params?.status) httpParams = httpParams.set('status', params.status);
    if (params?.personSearch) httpParams = httpParams.set('personSearch', params.personSearch);
    return this.http.get(`${this.baseUrl}/tudoc/failed-affiliations/export`, { params: httpParams, responseType: 'blob' });
  }
}
